import { parseAPIErrorResponse, parseStringtoNull } from "utils/utils";
import { useAuth } from "stores/auth/auth-context";
import { useToast } from "components/atoms/toast/toast-context";
import { IApiError } from "services/api/api.types";
import {
  UserType,
  CountryCode,
  Salutation,
  LoginClassType
} from "types/common/common.types";
import jwt_decode from "jwt-decode";
import { registerStudent } from "services/api/profiles/profileService";
import { ParentFormSchema, IUseParent } from "../use-profile-form.types";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { object, mixed, string, bool, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { phoneRegExp } from "constants/constants";
import trackEvent from "services/mixpanel/mixpanel";
import { webCheckoutEventNames } from "services/mixpanel/web-checkout-events";
import { createPayloadForWebCheckoutTrackingEvent } from "utils/mixpanel/web-checkout/web-checkout-mixpanel-utils";
import { constants } from "constants/constants";
import { IPackageAndTenureSummaryDetails } from "services/api/packages/packageTenureSummaryDetails.types";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { useGetCurrentPlatform } from "hooks/use-media-query/use-get-currrent-platform";
import { getSourceInfoEvent } from "utils/mixpanel/mixpanel-utils";
import * as Sentry from "@sentry/nextjs";

const FormSchema = object().shape({
  userType: mixed<UserType>().oneOf(Object.values(UserType)).required(),
  fullName: string().min(2, "Tulis minimal 2 karakter, ya.").required(),
  studentName: string().min(2, "Tulis minimal 2 karakter, ya. ").required(),
  studentNumber: string()
    .required("Mohon lengkapi nomor HP kamu")
    .matches(phoneRegExp, "Masukin nomor HP yang aktif, ya.")
    .min(8, "Masukin minimal 8 angka, ya.")
    .max(13, "Hmm... nomornya kepanjangan, nih."),
  grade: string().required("Isi bagian ini juga, ya."),
  email: string()
    .lowercase()
    .email("Hmm... alamat email ini sepertinya salah.")
    .required(),
  isParent: bool().required(),
  salutation: mixed<Salutation>().oneOf(Object.values(Salutation)).required(),
  classType: mixed<LoginClassType>()
    .oneOf(Object.values(LoginClassType))
    .required()
});

const useParentForm = (
  userType: UserType,
  summaryPackageData: IPackageAndTenureSummaryDetails | undefined,
  defaultGrade?: string
): IUseParent<ParentFormSchema> => {
  const router = useRouter();

  const { addToast } = useToast();

  const { user, signOutUser } = useAuth();
  const { userData, setUserData } = user;
  const [isLoading, setIsLoading] = useBoolean();

  const currentPlatform = useGetCurrentPlatform();

  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    setValue,
    watch
  } = useForm<InferType<typeof FormSchema>>({
    defaultValues: {
      userType: userType,
      salutation: Salutation.Bpk,
      isParent: false,
      grade: defaultGrade
    },
    mode: "onChange",
    delayError: constants.DELAY_ERROR,
    resolver: yupResolver(FormSchema)
  });

  const handleCheckbox = () => {
    if (getValues().isParent === true) {
      setValue("studentNumber", decodeJwtTokenFromRegisterToken() || "");
    } else {
      setValue("studentNumber", "");
    }
  };

  const onSubmit: SubmitHandler<ParentFormSchema> = async ({
    email,
    fullName,
    grade,
    salutation,
    studentName,
    studentNumber
  }) => {
    const payload = createPayloadForWebCheckoutTrackingEvent({
      useAuthData: user,
      Class: true,
      level: true,
      packageType: summaryPackageData?.name || null,
      tenureValue: summaryPackageData?.tenure_details.tenure_text.text || null,
      userFlow: true
    });
    trackEvent({
      eventName: webCheckoutEventNames.continueClickedOnCreateProfile,
      payload
    });
    try {
      if (userData.register_token) {
        setIsLoading.on();
        const res = await registerStudent({
          payload: {
            name: studentName,
            grade: grade.split("_")[0],
            stream: parseStringtoNull(grade.split("_")[1]),
            class_type: grade.split("_")[2],
            email: email.toLowerCase(),
            phone_number: studentNumber,
            country_code: CountryCode.Indonesia,
            notification_consent: user.isWhatsappConsentGiven,
            parent: {
              salutation,
              name: fullName,
              phone_number: decodeJwtTokenFromRegisterToken() || "",
              country_code: CountryCode.Indonesia
            }
          },
          token: userData.register_token,
          platform: getSourceInfoEvent(currentPlatform)
        });
        const {
          message,
          data: { parent_user, student_user }
        } = res;

        if (message === "OK") {
          await setUserData({
            ...userData,
            parent_user: {
              ...userData.parent_user,
              ...parent_user,
              students: [student_user]
            },
            primary_user: student_user,
            register_token: null
          });
          router.push({
            pathname: "/paket/user/summary",
            query: { ...router.query, user_id: userData.primary_user?.id }
          });
          setIsLoading.off();
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);
      const { message } = error as IApiError;
      addToast({
        heading: message,
        variant: "error"
      });
      setIsLoading.off();
    }
  };

  useEffect(() => {
    watch();
  });

  useEffect(() => {
    const userType = getValues().userType;
    user.setUserData({ ...user.userData, userType });
  }, [getValues().userType]);

  const decodeJwtTokenFromRegisterToken = () => {
    try {
      if (user && userData.register_token) {
        const number: { phoneNumber: string } = jwt_decode(
          userData.register_token
        );
        return number.phoneNumber;
      }
    } catch (err) {
      Sentry.captureException(err);
      signOutUser();
    }
  };

  return {
    control,
    handleSubmit,
    handleCheckbox,
    isValid,
    onSubmit,
    getPhoneNumberFromRegisterToken: decodeJwtTokenFromRegisterToken() || "",
    isLoading
  };
};

export { useParentForm };
